import { Icon } from "@aidkitorg/component-library";
import { BooleanExpr, RichText } from "@aidkitorg/types/lib/survey";
import { NavigationNode, BlockEdits } from "@aidkitorg/types/lib/translation/permissions";
import { Dialog, Transition } from '@headlessui/react';
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useHistory, useParams } from "react-router-dom";
import { get_deployment, get_rs_host, useAPIPost, usePost } from "./API";
import { ThreeColumnPage } from "./Components/ThreeColumnPage";
import { LanguageDropdown } from "./Components/LanguageDropdown";
import InterfaceContext, { AuthContext, ConfigurationContext, PublicConfigurationContext, SupportedLanguage } from "./Context";
import { ApplicantProvider } from './Contexts/ApplicantContext';
import { ScrollProvider } from "./Contexts/ScrollContext";
import { ViewportProvider } from "./Contexts/ViewportContext";
import { useAsyncEffect } from "./Hooks/AsyncEffect";
import { useModularMarkdown } from "./Hooks/ModularMarkdown";
import { useLocalizedStrings } from "./Localization";
import { ModularQuestionPage } from "./ModularQuestionPage";
import { InfoDict, QuestionOption } from "./Questions/Props";
import { removeSpecificCookies, useMarkdown } from "./Util";

export function useLinkKey() {
  const sessionStorage = useContext(InterfaceContext).sessionStorage;
  const cookieName = encodeURIComponent('key:' + window.location.pathname);
  const [cookies, setCookie,] = useCookies([cookieName]);
  const authContext = useContext(AuthContext);

  // Look for key in URL param first
  const params = new URLSearchParams(window.location.search);
  const key = params.get("key");
  const localId = params.get("localid");

  if (localId) {
    // for resuming unsubmitted apps from the portal. All the data is stored in the session storage already.
    authContext.setLocalId(localId);
    authContext.setToken(sessionStorage['auth:' + localId]);
    window.history.replaceState({}, document.title, window.location.pathname + localId);
    return sessionStorage['auth:' + localId];
  } else if (key) {
    setCookie(cookieName, key);
    // Remove the key from the URL and history
    const params = new URLSearchParams(window.location.search);
    params.delete("key");
    window.history.replaceState({}, document.title, `${window.location.pathname}?${params.toString()}`);
    return key;
  }

  return cookies[cookieName];
}

function ContinueModal(props: { continue: () => void, restart: () => void }) {
  const [open, setOpen] = useState(true)
  const cancelButtonRef = useRef(null)
  const L = useLocalizedStrings();

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={cancelButtonRef} onClose={setOpen}>
        <div className="flex items-start justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >

            <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all z-50 sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                    {L.apply.application_in_progress_found}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {L.apply.would_you_like_to_continue}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 sm:ml-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={() => { props.continue(); setOpen(false) }}
                  ref={cancelButtonRef}
                >
                  {L.apply.continue_where_i_left_off}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => { props.restart(); setOpen(false) }}
                >
                  {L.apply.start_new_application}
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

function SubmittingModal(props: { content: JSX.Element }) {
  const [open, setOpen] = useState(true)
  const cancelButtonRef = useRef(null)
  const L = useLocalizedStrings();

  function close() {
    window.location.reload();
    return false;
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={cancelButtonRef} onClose={close}>
        <div className="flex items-start justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >

            <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 overflow-hidden shadow-xl transform transition-all z-50">
              <div className="">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <ArrowPathIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                    {L.apply.submitting}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {L.apply.submitting_your_information}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export function AllDoneComponent(props: {
  content: JSX.Element,
  customSubmittedModal?: {
    title?: RichText,
    message?: RichText
  },
  info?: InfoDict,
}) {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const configuration = useContext(ConfigurationContext);
  const message =
    props.customSubmittedModal?.message?.[context.lang]
    || props.customSubmittedModal?.message?.en
    || configuration.application_submit_message
    || L.apply.your_application_has_been_received;

  // If we have info, allow substitutions
  const marked = (props.info)
    ? useModularMarkdown({ content: message, info: props.info })
    : useMarkdown(message);

  return (
    <div className="max-w-4xl mx-auto applicant-led pb-36 pt-10 sm:pt-20">
      <div className="">
        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
          <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
        </div>
        <div className="mt-4 text-center sm:mt-0 sm:text-left px-5">
          <h1 className="text-lg leading-6 font-medium text-gray-900">
            {props.customSubmittedModal?.title
              ? <span>{(props.customSubmittedModal.title[context.lang as SupportedLanguage] || props.customSubmittedModal.title['en'])}</span>
              : <span>{L.apply.all_done}</span>}
          </h1>
          <div className="mt-3">
            <fieldset>
              <legend className="text-gray-500">
                {marked}
              </legend>
            </fieldset>
          </div>
        </div>
      </div>
    </div>
  )
}

type MappedContentTypes = {
  [Language in SupportedLanguage]?: string
}

export type Question = {
  'Field Type': string,
  Question: string,
  'English Content': string,
  'Spanish Content': string,
  'Target Field'?: string,
  "Options (if relevant)"?: QuestionOption[],
  'Conditional On'?: string,
  'Conditional On Value'?: string[],
  Metadata?: string,
  'Additional Options'?: string[],
  computeEnabled: (info: InfoDict) => boolean | Promise<boolean>,
  currentlyEnabled?: boolean,
  complete?: boolean,
  lastVisibleQuestion?: boolean
}

export type Sections = {
  info?: InfoDict,
  branding?: {
    name?: string,
    logo?: string,
  },
  options?: Record<string, any>,
  sections?: Array<MappedContentTypes & {
    id: string,
    Question: string,
    "Field Type": string,
    'English Content': string,
    'Spanish Content': string,
    complete?: boolean,
    currentlyEnabled?: boolean,
    computeVisible: (info: InfoDict) => boolean | Promise<boolean>,
    currentlyVisible?: boolean,
    hideNextButton?: boolean,

    'Conditional On'?: string,
    'Conditional On Value'?: string,

    'Metadata'?: {
      'dashboard'?: boolean,
      /** declared in distro config */
      sectionId?: string
    }
    Questions: Question[]
  }>,
}

type WarnOnIncompleteSurvey = {
  submissionField?: string,
  warningMessage?: RichText
};

type FooterItem = {
  content: RichText;
  link?: string;
  icon?: "EnvelopeIcon" | "PhoneIcon" | "QuestionMarkCircleIcon" | "ShieldCheckIcon";
  hideOnSmallScreen: boolean;
  hideOnLargeScreen: boolean;
  links?: FooterItem[];
};

export function FooterLink({ item, isNested }: { item: FooterItem, isNested?: boolean }) {
  const context = useContext(InterfaceContext);

  const innerContent = <div className={`text-sm flex gap-1 items-center justify-center md:justify-start ${!isNested ? 'font-bold' : ''}`}>
    {item.icon && <Icon name={item.icon} size="md" />}
    {useMarkdown(item.content[context.lang] || item.content["en"], false, true)}
  </div>;

  if (item.link) {
    return <a href={item.link} target={/^https?:\/\//.test(item.link) ? "_blank" : undefined} className="text-inherit inline-block">{innerContent}</a>
  }
  return innerContent;
}

export function SubsectionPage() {
  const params = new URLSearchParams(window.location.search);
  const { path } = useParams() as Record<string, string>;
  const key = useLinkKey();
  const viewInfo = { ...JSON.parse(params.get("view_info") || '{}') } as Record<string, string | undefined>;
  const get_survey = usePost("/subsurvey/definition");
  const getProtectedSearchCurrentApplicantInfo = usePost("/protected_search/get_current_applicant_info");
  const L = useLocalizedStrings();

  const initialInfo = {} as Record<string, string>;

  const specialKeys = ['key', 'lang', 'view_info'];

  for (const paramKey of Array.from(params.keys())) {
    if (paramKey && !specialKeys.includes(paramKey)) {
      initialInfo[paramKey] = params.get(paramKey)!;
    }
  }

  const [info, setInfo] = useState<InfoDict>(initialInfo);
  const [protectedSearchCurrentApplicantInfo, setProtectedSearchCurrentApplicantInfo] = useState<{ applicant?: string }>({});
  const [resp, setResp] = useState({} as Sections);
  const saveInfoRS = useAPIPost(get_rs_host() + "/compute_and_save");
  const [showComplete, setShowComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const registeredTimeout = useRef<undefined | ReturnType<typeof setTimeout>>(undefined);
  const context = useContext(InterfaceContext);
  // If we're on /p/apply with no key, redirect to /apply
  const [redirect, setRedirect] = useState(path === 'apply' && !key ? '/apply' : null as null | string);
  const history = useHistory();
  const shouldFastForward = useRef(false);
  const blockFurtherEdits = useRef<BlockEdits>({} as BlockEdits);
  const [infoPendingSave, setInfoPendingSave] = useState<InfoDict | null>(null);
  const warnOnIncompleteSurvey = useRef<WarnOnIncompleteSurvey>({});
  const publicConfig = useContext(PublicConfigurationContext);
  const [nav, setNav] = useState<NavigationNode[]>([]);

  // Have to pass document cookie directly to usePost because it doesn't update when the cookie changes
  // This is a hacky workaround and will only update on rerenders. But react doesn't track cookies.
  const getNav = usePost("/applicant/navigation", { token: () => document.cookie.split("; ").find((c) => c.startsWith("portal="))?.split("=")[1] || '' });

  useAsyncEffect(async () => {
    const response = await getProtectedSearchCurrentApplicantInfo({});
    setProtectedSearchCurrentApplicantInfo(response || {});
  }, []);

  useAsyncEffect(async (cancel) => {
    // Fetch navigation
    const response = await getNav({ lang: context.lang }, cancel);
    setNav(response.navigation);
    if (response.loggedIn === false) {
      // remove portal token if it's expired. No need to alert the user here though, just treat them as logged out.
      removeSpecificCookies(['portal']);
    }
  }, [document.cookie, getNav, context.lang]);

  useEffect(() => {
    if (redirect) history.push(redirect);

    return () => {
      if (registeredTimeout.current) {
        clearTimeout(registeredTimeout.current);
      }
    }
  }, [redirect]);

  useEffect(() => {
    (async () => {
      const response = await get_survey({
        form_name: path
      });

      if (response && response.error) {
        if (response.copy) {
          context.setForbiddenCopy((prevState) => ({
            ...prevState,
            [path]: response.copy as any
          }));
        }
        /** We do this setRedirect to avoid any state leaks because this is in an async useEffect */
        if (response.error === 'subsurvey_expired') {
          setRedirect('/ahh/expired?path=' + path);
        }
        if (response.error === 'subsurvey_part_mismatch' || response.error === 'subsurvey_part_required') {
          setRedirect('/ahh/inaccessible?path=' + path);
        }
        if (response.error === 'subsurvey_not_found') {
          setRedirect('/404');
        }
      }

      if (response && response.sections) {
        // First map the result as the response omitting error after making things required again
        const result = response as Omit<Required<Awaited<ReturnType<typeof get_survey>>>, 'error'>;
        setResp(result as unknown as Sections); // hacky

        if (response?.needsKey && !key) {
          window.location.pathname = '401';
        }

        if (response?.initialInfo) {
          setInfo({ ...response.initialInfo, ...initialInfo });
        }

        if (response.options?.should_fast_forward) {
          shouldFastForward.current = true;
        }

        if (response.options?.block_further_edits) {
          blockFurtherEdits.current = response.options.block_further_edits;
        }

        if (response.options?.warnOnIncompleteSurvey) {
          warnOnIncompleteSurvey.current = {
            submissionField: response.options.warnOnIncompleteSurvey.submissionField,
            warningMessage: response.options.warnOnIncompleteSurvey.warningMessage,
          }
        }
      }
    })();
  }, [key]);

  useEffect(() => {
    // No Saving for now on status pages
    if (path === 'status') return;
    if (!key) return;

    if (registeredTimeout.current) {
      clearTimeout(registeredTimeout.current);
    }
    setInfoPendingSave(info);
    const timeout = setTimeout(async () => {
      registeredTimeout.current = undefined;
      if (redirect) return;
      console.log("Saving keys: ", Object.entries(info || {}).map(([k, v]) => k + ': ' + (v || '').length).join(','));
      if (process.env.NODE_ENV === 'development') {
        console.log("Saving keys", info);
      }
      await saveInfoRS({
        deploymentKey: get_deployment(),
        changedKeys: info,
        token: key
      });
      setInfoPendingSave(null);
    }, 3000);

    registeredTimeout.current = timeout;
  }, [info]);

  const refreshInfo = useCallback(async () => {
    const response = await get_survey({ form_name: path })
    if (response?.initialInfo) {
      if (process.env.NODE_ENV === 'development') console.log("Setting info", response.initialInfo, "plus", infoPendingSave);
      setInfo({ ...response.initialInfo, ...infoPendingSave });
    }
  }, [path, infoPendingSave]);

  const Submit = async (submit_key?: string, options?: { info: InfoDict }) => {
    if (registeredTimeout.current) clearTimeout(registeredTimeout.current);
    console.log("Saving from submit");
    const resp = await saveInfoRS({
      deploymentKey: get_deployment(),
      changedKeys: (submit_key ? { ...info, [submit_key]: (new Date()).toISOString() } : info) || {},
      token: key,
      ...(submit_key ? { submit_key } : {})
    });
    if (resp.value) {
      setShowComplete(true);
    }
    return resp;
  };

  const checkpoint = async (params: { form_name: string, info: Record<string, string | undefined> }) => {
    console.log("Saving via compute_and_save");
    await saveInfoRS({
      deploymentKey: get_deployment(),
      changedKeys: params.info,
      token: key
    });
    return { status: 'ok' };
  };

  if (showComplete) {
    const main = <AllDoneComponent
      info={info}
      content={<></>}
      customSubmittedModal={resp.options?.custom_submitted_modal}
    />;

    return publicConfig.experimental?.enableApplicantPortal ? <ThreeColumnPage main={main} nav={nav} Viewer={'applicant'} applicantPortal /> : main;
  }

  if (submitting) {
    return <SubmittingModal content={<></>} />;
  }

  const main = <ModularQuestionPage
    sections={resp}
    viewInfo={viewInfo}
    submit={Submit}
    checkpoint={checkpoint}
    branding={resp.branding}
    saveInfo={async (info) => {

    }}
    saveAuth={(auth) => {

    }}
    shouldFastForward={shouldFastForward}
    sequential={true}
    blockFurtherEdits={blockFurtherEdits.current}
    warnOnIncompleteSurvey={warnOnIncompleteSurvey.current}
    applicantPortal={publicConfig.experimental?.enableApplicantPortal}
    Viewer={'applicant'}
  />

  return <ApplicantProvider context={{ info, update: setInfo, refreshInfo }}>
    {protectedSearchCurrentApplicantInfo?.applicant &&
      <div className="sticky top-0 py-1 w-full z-[100] text-center bg-yellow-100 text-yellow-800">
        {L.applicant_lookup.protected_search_viewing_application_for.replace('$applicant', protectedSearchCurrentApplicantInfo.applicant)}
      </div>
    }
    {publicConfig.experimental?.enableApplicantPortal ?
      <ThreeColumnPage main={main} nav={nav} Viewer={'applicant'} applicantPortal />
      : main
    }
  </ApplicantProvider>;

}

function ApplicationPage() {
  const { localid } = useParams<{ localid?: string, dynamoAppId?: string }>();
  const params = new URLSearchParams(window.location.search);
  const localId = useRef(localid)
  const auth = useRef('');
  const configuration = useContext(ConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const programName = publicConfig.name || configuration.program_name;
  const [originalParams, setOriginalParams] = useState<URLSearchParams | null>(null);
  const context = useContext(InterfaceContext);
  const sessionStorage = context.sessionStorage;
  const marked = useMarkdown(
    publicConfig.application?.guard?.content?.[context.lang || 'en'] || // Distro guard content if available
    configuration.guard_content || // Legacy Airtable guard content
    '' // Fallback to empty string
  );

  useEffect(() => {
    const fullUrl = `${window.location.origin}${location.pathname}${location.search}`;
    setOriginalParams(new URLSearchParams(new URL(fullUrl).search));
  }, []);

  if (!programName) {
    return <></>;
  }

  const { guard: distroGuard } = publicConfig.application || {};
  // Use Distro guard if configured
  if (distroGuard?.enabled) {
    const hasGuardParam = distroGuard.accessParams?.some((p) => originalParams?.has(p));
    if (!hasGuardParam) {
      return (
        <div>
          <div className="px-4 py-4">
            <LanguageDropdown languages={(configuration.languages || 'en,es').split(',')} />
          </div>
          <div className="mx-auto px-4 py-2 max-w-4xl">
            {marked}
          </div>
        </div>
      )
    }
  }

  // Fallback to legacy Airtable guard config
  let gparams = (configuration.guard_params || '').split(',').map((p: string) => p.trim());
  const hasGuardParam = gparams.some((p: string) => params.has(p));
  const originallyHadGuardParam = gparams.some((p: string) => originalParams?.has(p));

  if (configuration.guard_params && (!hasGuardParam && !originallyHadGuardParam)) {
    return <div className="m-12 mx-auto w-96 mt-10 bg-white overflow-hidden shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6 guard">
        {marked}
      </div>
    </div>
  }

  return <AuthContext.Provider value={{
    localId: () => {
      return localId.current!;
    },
    token: () => {
      return auth.current;
    },
    setToken: (token) => {
      sessionStorage['auth:' + localId.current] = token;
      auth.current = token!;
    },
    setLocalId: (id) => {
      localId.current = id;
    }
  }}>
    <ApplicationPageInner />
  </AuthContext.Provider>;
}

function ApplicationPageInner() {
  const params = new URLSearchParams(window.location.search);
  const key = useLinkKey();
  const get_survey = usePost("/subsurvey/definition");
  const submit = usePost("/subsurvey/submit");
  const getCurrentInfo = usePost("/subsurvey/resume");

  const [applicationSections, setApplicationSections] = useState({} as Sections);
  const urlParams = new URLSearchParams(window.location.hash.slice(1));
  const authContext = useContext(AuthContext);
  const context = useContext(InterfaceContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const sessionStorage = context.sessionStorage;
  const localStorage = context.localStorage;

  const history = useHistory();
  const checkpoint = usePost("/subsurvey/checkpoint");

  const [askToContinue, setAskToContinue] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const shouldFastForward = useRef(false);
  const blockFurtherEdits = useRef<BlockEdits>({} as BlockEdits);
  const warnOnIncompleteSurvey = useRef<WarnOnIncompleteSurvey>({});
  const registeredTimeout = useRef<undefined | ReturnType<typeof setTimeout>>(undefined);

  const toSave = ['ref', 'propel_id', '_local_id'];
  const initialInfo: InfoDict = {};
  const L = useLocalizedStrings();
  for (const key of Array.from(params.keys())) {
    if (toSave.includes(key)) {
      initialInfo[key] = params.get(key)!;
    }
  }

  const logoutAndShowComplete = useCallback(() => {
    // clear session storage for, and log out of, this application
    const localId = authContext.localId?.();
    authContext.setToken(null);
    sessionStorage.removeItem('cache:' + localId);
    sessionStorage.removeItem('rev:' + localId);
    sessionStorage.removeItem('auth:' + localId);
    sessionStorage.removeItem('saved:' + localId);
    // show complete message to the user
    setShowComplete(true);
  }, [authContext.localId, sessionStorage]);

  const [info, setInfo] = useState<InfoDict>(initialInfo);

  const [nav, setNav] = useState<NavigationNode[]>([]);

  // Have to pass document cookie directly to usePost because it doesn't update when the cookie changes
  // This is a hacky workaround and will only update on rerenders. But react doesn't track cookies.
  const getNav = usePost("/applicant/navigation", { token: () => document.cookie.split("; ").find((c) => c.startsWith("portal="))?.split("=")[1] || '' });

  useEffect(() => {
    // Fetch navigation
    (async () => {
      const response = await getNav({ lang: context.lang });
      setNav(response.navigation);
      if (response.loggedIn === false) {
        // remove portal token if it's expired. No need to alert the user here though, just treat them as logged out.
        removeSpecificCookies(['portal']);
      }
    })();
  }, [document.cookie, getNav, context.lang]);

  useEffect(() => {
    (async () => {
      const resp = await get_survey({ form_name: 'apply' });

      setApplicationSections(resp as unknown as Sections);
      if (resp?.options?.block_further_edits) {
        blockFurtherEdits.current = resp.options.block_further_edits;
      }

      if (resp?.options?.warnOnIncompleteSurvey) {
        warnOnIncompleteSurvey.current = resp.options.warnOnIncompleteSurvey;
      }

      if (authContext.localId?.() && sessionStorage['cache:' + authContext.localId()]) {
        if (sessionStorage['auth:' + authContext.localId()]) {
          authContext.setToken(sessionStorage['auth:' + authContext.localId()]);
          // TODO: Check if we should resume
        }
        setInfo(JSON.parse(sessionStorage['cache:' + authContext.localId()]));
        console.log("Fast forwarding");
        shouldFastForward.current = true;
        // TODO: resume on page load (if revision is older)
      } else {
        setInfo(initialInfo);
      }
    })();
  }, [key]);

  useEffect(() => {
    if (!authContext.localId) return;
    if (registeredTimeout.current) {
      clearTimeout(registeredTimeout.current);
    }
    registeredTimeout.current = setTimeout(async () => {
      registeredTimeout.current = undefined;
      if (authContext.token?.() && authContext.localId?.()) {
        // TODO: This will try to save before we've authed at the moment
        if (sessionStorage['rev:' + authContext.localId()] && sessionStorage['rev:' + authContext.localId()] !== sessionStorage['saved:' + authContext.localId()]) {
          const checkpointResponse = await checkpoint({
            form_name: 'apply',
            info: info || {},
          });
          if (checkpointResponse.status === 'ok') {
            sessionStorage['saved:' + authContext.localId()] = sessionStorage['rev:' + authContext.localId()];
          } else if (checkpointResponse.status === 'already_submitted' && !blockFurtherEdits.current?.block_when) {
            // If blockFurtherEdits is set, let that handle the already submitted case. 
            // There is a configured message OR a readonly option that could be used here.
            logoutAndShowComplete();
          }
        }
      }
      console.log("Saving")
    }, 3000);
  }, [info]);

  const Submit = async (submit_key?: string) => {
    const resp = await submit({
      form_name: 'apply',
      info: (submit_key ? { ...info, [submit_key]: (new Date()).toISOString() } : info) || {},
      language: context.lang,
      ...(submit_key ? { submit_key } : {})
    });
    if (resp.status === 'ok' || resp.status === 'already_submitted') {
      logoutAndShowComplete();
    }
    return resp;
  }

  if (showComplete) {
    const main = <AllDoneComponent
      content={<></>}
      info={info}
      customSubmittedModal={applicationSections?.options?.custom_submitted_modal}
    />
    return publicConfig.experimental?.enableApplicantPortal ? <ThreeColumnPage main={main} nav={nav} Viewer={'applicant'} applicantPortal /> : main;
  }

  if (askToContinue) {
    return <ContinueModal restart={() => {
      sessionStorage.deleteItem('pendingInfo');
      setInfo({})
      setAskToContinue(false);
    }} continue={() => {
      const pendingInfo = JSON.parse(sessionStorage['pendingInfo']);
      shouldFastForward.current = true;
      setInfo(pendingInfo);
      setAskToContinue(false);
    }} />
  }

  const update = (newInfo: InfoDict) => {
    newInfo['_lang'] = context.lang;
    let id = authContext?.localId?.();
    if (!id) {
      id = Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 5);
      authContext.setLocalId(id);
      const applyLang = params.get("lang") || context.lang;
      history.push('/apply/' + id + '?lang=' + applyLang);
    }

    newInfo._local_id = id;

    const newSerializedInfo = JSON.stringify(newInfo);
    if (sessionStorage['cache:' + id] != newSerializedInfo) {
      sessionStorage['cache:' + id] = newSerializedInfo;
      sessionStorage['rev:' + id] = sessionStorage['rev:' + id] ? parseInt(sessionStorage['rev:' + id]) + 1 : 1;
    }
    setInfo(newInfo);
  }

  const refreshInfo = async () => {
    shouldFastForward.current = true;
    let curResponse = await getCurrentInfo({ form_name: "apply" })
    if (curResponse.info) setInfo(curResponse.info);
  };

  const main = <ModularQuestionPage
    sections={applicationSections}
    saveAuth={(auth) => {
      authContext.setToken(auth!);
    }}
    saveInfo={async (info) => {
      //console.log("SaveInfo");
    }}
    submit={Submit}
    checkpoint={checkpoint}
    loadInfo={(info, token, submitted) => {
      if (submitted && !blockFurtherEdits.current?.block_when) {
        // Once an app is submitted it shouldn't be accessed from this page.
        // BlockFurtherEdits is a way to customize this behavior - either a custom blocked message 
        // or a readonly option.
        logoutAndShowComplete();
        return;
      }
      // Redirect to a new local id
      const id = Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 5);
      authContext.setLocalId(id);
      authContext.setToken(token!);
      history.push('/apply/' + id);

      shouldFastForward.current = true;
      setInfo(info);
    }}
    shouldFastForward={shouldFastForward}
    sequential={true}
    blockFurtherEdits={blockFurtherEdits.current}
    warnOnIncompleteSurvey={warnOnIncompleteSurvey.current}
    applicantPortal={publicConfig.experimental?.enableApplicantPortal}
    Viewer={'applicant'}
  />;

  return <ApplicantProvider context={{ info, update, refreshInfo }}>
    {
      publicConfig.experimental?.enableApplicantPortal ?
        <ThreeColumnPage nav={nav} main={main} Viewer={'applicant'} applicantPortal />
        : main
    }
  </ApplicantProvider>
}

export default ApplicationPage;
